<template>
  <div class="settings-page">
    <section v-if="!isPlansModule" class="resource-toolbar">
      <div>
        <h2>{{ pageTitle }}</h2>
        <p>{{ pageSubtitle }}</p>
      </div>
      <v-btn v-if="isPaymentSettingsModule" depressed color="primary" :loading="saving" @click="save">
        <v-icon left size="18">mdi-content-save-outline</v-icon>
        Salvar
      </v-btn>
    </section>

    <section v-if="isPaymentSettingsModule" class="settings-panel payment-methods-panel">
      <div class="panel-heading split-heading">
        <div>
          <span class="section-kicker">Cobrança dos associados</span>
          <h3>Meios aceitos nas faturas</h3>
          <p>Defina como moradores e responsáveis poderão pagar as mensalidades do local.</p>
        </div>
        <v-select
          v-model="form.default_payment_method"
          :items="availableDefaultMethods"
          label="Forma padrão"
          outlined
          dense
          hide-details="auto"
          class="payment-default-select"
        />
      </div>

      <div class="payment-method-grid">
        <label class="payment-method-option" :class="{ active: form.allow_pix }">
          <span class="method-icon"><v-icon size="22">mdi-qrcode</v-icon></span>
          <span>
            <strong>PIX</strong>
            <small>Pagamento rápido por código Pix.</small>
          </span>
          <v-switch v-model="form.allow_pix" inset color="primary" hide-details />
        </label>
        <label class="payment-method-option" :class="{ active: form.allow_boleto }">
          <span class="method-icon"><v-icon size="22">mdi-barcode</v-icon></span>
          <span>
            <strong>Boleto</strong>
            <small>Linha digitável para pagamento bancário.</small>
          </span>
          <v-switch v-model="form.allow_boleto" inset color="primary" hide-details />
        </label>
        <label class="payment-method-option" :class="{ active: form.allow_credit_card }">
          <span class="method-icon"><v-icon size="22">mdi-credit-card-outline</v-icon></span>
          <span>
            <strong>Cartão de crédito</strong>
            <small>Pagamento avulso com cartão.</small>
          </span>
          <v-switch v-model="form.allow_credit_card" inset color="primary" hide-details />
        </label>
        <label class="payment-method-option" :class="{ active: form.allow_recurring_credit_card, disabled: !form.allow_credit_card }">
          <span class="method-icon"><v-icon size="22">mdi-autorenew</v-icon></span>
          <span>
            <strong>Recorrência no cartão</strong>
            <small>Disponível quando cartão estiver habilitado.</small>
          </span>
          <v-switch
            v-model="form.allow_recurring_credit_card"
            inset
            color="primary"
            hide-details
            :disabled="!form.allow_credit_card"
          />
        </label>
      </div>

      <v-alert dense text type="info" class="mt-4 mb-0">
        A fatura será criada no sistema e o associado escolhe entre os meios habilitados.
      </v-alert>
    </section>

    <section v-if="isAccountModule" class="account-flow">
      <section class="settings-panel account-status-panel">
        <div class="account-status-copy">
          <span class="account-status-icon">
            <v-icon size="24">mdi-lock-alert-outline</v-icon>
          </span>
          <div>
            <span class="section-kicker">Conta BOARUA</span>
            <h3>{{ planChargeStatusLabel }}</h3>
            <p>{{ planStatusAlert ? planStatusAlert.message : "Escolha um plano, gere a cobrança e acompanhe a liberação da associação." }}</p>
          </div>
        </div>
        <div class="account-status-total">
          <span>Valor estimado</span>
          <strong>{{ money(planEstimate.total) }}/mês</strong>
        </div>
      </section>

      <section class="settings-panel account-step-panel account-final-panel">
        <div class="account-step-head">
          <span class="step-number">1</span>
          <div>
            <h3>Sua assinatura</h3>
            <p>Veja o plano atual e acesse a página de planos quando precisar contratar ou trocar.</p>
          </div>
          <v-btn depressed color="primary" to="/configuracoes/planos">
            <v-icon left size="18">mdi-crown-outline</v-icon>
            Ver planos
          </v-btn>
        </div>

        <div class="account-final-summary">
          <div>
            <span>Status</span>
            <strong>{{ planChargeStatusLabel }}</strong>
          </div>
          <div>
            <span>Total estimado</span>
            <strong>{{ money(planEstimate.total) }}</strong>
          </div>
          <div>
            <span>Vencimento</span>
            <strong>{{ formatDate(activePlanCharge && activePlanCharge.due_date) }}</strong>
          </div>
        </div>

        <div class="account-primary-actions">
          <v-btn outlined color="primary" :loading="loadingPlanCharge" @click="refreshPlanCharge">
            <v-icon left size="18">mdi-refresh</v-icon>
            Atualizar status
          </v-btn>
        </div>

        <div v-if="activePlanCharge" class="plan-payment-result">
          <div v-if="activePlanCharge.status === 'paid'" class="plan-paid-state">
            <v-icon size="20">mdi-check-circle-outline</v-icon>
            Pagamento confirmado. A plataforma está liberada para uso.
          </div>

          <template v-else>
            <button type="button" class="plan-payment-link" @click="planPaymentDialog = true">
              <v-icon size="16">mdi-credit-card-check-outline</v-icon>
              Ver pagamento
            </button>
            <button v-if="activePlanCharge.pix_copy_paste" type="button" class="plan-copy-button" @click="copyPlanPix">
              <v-icon size="16">mdi-content-copy</v-icon>
              Copiar código Pix
            </button>
            <button v-if="activePlanCharge.boleto_barcode" type="button" class="plan-copy-button" @click="copyPlanBoleto">
              <v-icon size="16">mdi-barcode</v-icon>
              Copiar código do boleto
            </button>
          </template>
        </div>
      </section>
    </section>

    <section v-if="isPlansModule" class="plans-page">
      <header class="plans-hero">
        <h2>Planos simples e transparentes</h2>
        <p>Escolha o plano ideal para o tamanho do seu local. Sem contrato longo e sem surpresa.</p>
      </header>

      <div class="plans-grid">
        <button
          v-for="plan in visibleBoaruaPlans"
          :key="plan.key"
          type="button"
          :class="['pricing-choice-card', { selected: form.boarua_plan_key === plan.key }]"
          @click="openPlanCheckout(plan.key)"
        >
          <span class="pricing-choice-title">
            <strong>{{ plan.name }}</strong>
            <span v-if="plan.recommended" class="pricing-choice-badge">Mais popular</span>
          </span>
          <span v-if="plan.price !== null" class="pricing-choice-price">
            {{ money(planEffectivePrice(plan)) }}<small>/mês</small>
          </span>
          <span v-else class="pricing-choice-price custom">Sob consulta</span>
          <p>{{ planSummary(plan) }}</p>
          <span class="pricing-choice-lines">
            <span><v-icon size="16">mdi-check-circle</v-icon>{{ planResidencesLabel(plan) }} residências</span>
            <span><v-icon size="16">mdi-check-circle</v-icon>{{ planCommercesLabel(plan) }} comércios</span>
            <span><v-icon size="16">mdi-check-circle</v-icon>Suporte e gestão BOARUA</span>
            <span><v-icon size="16">mdi-check-circle</v-icon>Cobranças e operação integradas</span>
          </span>
          <em>{{ form.boarua_plan_key === plan.key ? "Plano selecionado" : "Escolher plano" }}</em>
        </button>
      </div>
    </section>

    <section v-if="isPaymentSettingsModule" class="settings-panel mt-4">
      <div class="panel-heading payment-account-heading">
        <div>
          <h3>Conta de recebimento BoaRua</h3>
          <p>A cobrança do associado é emitida pela conta do local. O plano BOARUA é tratado separadamente.</p>
        </div>
        <v-tooltip bottom :disabled="!paymentAccountErrorTooltip">
          <template v-slot:activator="{ on, attrs }">
            <v-chip small label :color="paymentAccountStatus.color" text-color="white" v-bind="attrs" v-on="on">
              {{ paymentAccountStatus.label }}
            </v-chip>
          </template>
          <span>{{ paymentAccountErrorTooltip }}</span>
        </v-tooltip>
      </div>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field v-model="paymentAccount.legal_name" label="Razão social" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="paymentAccount.trade_name" label="Nome público/fantasia" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            :value="formattedPaymentDocument"
            v-mask="$masks.cnpj"
            label="CNPJ"
            outlined
            dense
            hide-details="auto"
            :disabled="paymentAccountLocked"
            @input="paymentAccount.document = $event"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field v-model="paymentAccount.email" label="E-mail financeiro" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="paymentAccount.phone"
            v-mask="$masks.phone"
            label="Telefone"
            outlined
            dense
            hide-details="auto"
            :disabled="paymentAccountLocked"
          />
        </v-col>
        <v-col cols="12">
          <div class="form-section-title">
            <v-icon size="18">mdi-map-marker-outline</v-icon>
            Endereço fiscal
          </div>
        </v-col>
        <v-col cols="12" md="7">
          <v-text-field v-model="paymentAccount.address" label="Endereço fiscal" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field v-model="paymentAccount.address_number" label="Número" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="paymentAccount.postal_code"
            v-mask="$masks.zipCode"
            :loading="cepLoading"
            label="CEP"
            outlined
            dense
            hide-details="auto"
            :disabled="paymentAccountLocked"
            @blur="loadPaymentAddressByCep"
          />
        </v-col>
        <v-col cols="12" md="5">
          <v-text-field v-model="paymentAccount.province" label="Bairro" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12" md="5">
          <v-text-field v-model="paymentAccount.city" label="Cidade" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field v-model="paymentAccount.state" label="UF" maxlength="2" outlined dense hide-details="auto" :disabled="paymentAccountLocked" />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="paymentAccount.gateway_wallet_id"
            label="Identificador da conta de recebimento"
            outlined
            dense
            hide-details="auto"
            placeholder="Preenchido automaticamente ou informado ao vincular conta existente"
            :disabled="paymentAccountLocked && !requiresAsaasConnection"
          />
        </v-col>
        <v-col v-if="requiresAsaasConnection" cols="12">
          <v-text-field
            v-model="paymentAccount.gateway_api_key"
            :append-icon="showAsaasApiKey ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
            :type="showAsaasApiKey ? 'text' : 'password'"
            label="Credencial da conta de recebimento"
            outlined
            dense
            hide-details="auto"
            @click:append="showAsaasApiKey = !showAsaasApiKey"
          />
        </v-col>
      </v-row>

      <v-alert v-if="requiresAsaasConnection" dense text type="info" class="mt-4 mb-0">
        Este CNPJ já possui uma conta de recebimento. O cadastro do local foi mantido; para ativar cobranças, vincule a conta existente informando a credencial e o identificador.
      </v-alert>

      <div v-if="showPaymentAccountActions" class="payment-account-actions">
        <v-btn v-if="!paymentAccountExists" outlined color="primary" :loading="savingAccount" @click="savePaymentAccount">
          Salvar dados
        </v-btn>
        <v-btn
          v-if="requiresAsaasConnection"
          depressed
          color="primary"
          :loading="connectingExistingAccount"
          @click="connectExistingAsaas"
        >
          Vincular conta existente
        </v-btn>
        <v-btn
          v-else-if="!paymentAccountExists"
          depressed
          color="primary"
          :loading="creatingSubaccount"
          @click="createAsaasSubaccount"
        >
          Criar conta de recebimento
        </v-btn>
      </div>
    </section>

    <section v-if="isPaymentSettingsModule" class="settings-panel mt-4">
      <div class="panel-heading">
        <h3>Conta de repasse</h3>
        <p>Informe para onde o saldo do local poderá ser transferido após receber as mensalidades.</p>
      </div>

      <v-row dense>
        <v-col cols="12" md="4">
          <v-select v-model="paymentAccount.payout_type" :items="payoutTypes" label="Tipo de repasse" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'pix'" cols="12" md="4">
          <v-select v-model="paymentAccount.payout_pix_key_type" :items="pixKeyTypes" label="Tipo de chave Pix" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'pix'" cols="12" md="4">
          <v-text-field v-model="paymentAccount.payout_pix_key" label="Chave Pix de repasse" outlined dense hide-details="auto" />
        </v-col>

        <v-col v-if="paymentAccount.payout_type === 'bank_account'" cols="12" md="3">
          <v-text-field v-model="paymentAccount.payout_bank_code" label="Código do banco" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'bank_account'" cols="12" md="5">
          <v-text-field v-model="paymentAccount.payout_bank_name" label="Nome do banco" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'bank_account'" cols="12" md="3">
          <v-text-field v-model="paymentAccount.payout_agency" label="Agência" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'bank_account'" cols="12" md="2">
          <v-text-field v-model="paymentAccount.payout_agency_digit" label="Dígito" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'bank_account'" cols="12" md="3">
          <v-text-field v-model="paymentAccount.payout_account" label="Conta" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'bank_account'" cols="12" md="2">
          <v-text-field v-model="paymentAccount.payout_account_digit" label="Dígito" outlined dense hide-details="auto" />
        </v-col>
        <v-col v-if="paymentAccount.payout_type === 'bank_account'" cols="12" md="2">
          <v-select v-model="paymentAccount.payout_account_type" :items="accountTypes" label="Tipo" outlined dense hide-details="auto" />
        </v-col>
        <v-col cols="12" md="7">
          <v-text-field v-model="paymentAccount.payout_holder_name" label="Titular da conta" outlined dense hide-details="auto" />
        </v-col>
        <v-col cols="12" md="5">
          <v-text-field
            v-model="paymentAccount.payout_holder_document"
            v-mask="((paymentAccount.payout_holder_document || '').replace(/\D/g, '').length <= 11) ? $masks.cpf : $masks.cnpj"
            label="CPF/CNPJ do titular"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <div class="payment-account-actions">
        <v-btn outlined color="primary" :loading="savingAccount" @click="savePaymentAccount">
          Salvar repasse
        </v-btn>
      </div>
    </section>

    <section v-if="isPaymentSettingsModule" class="settings-panel mt-4">
      <div class="panel-heading">
        <h3>Geração automática</h3>
        <p>As faturas mensais serão sempre criadas automaticamente pelos workers compartilhados.</p>
      </div>

      <div class="settings-grid">
        <v-switch v-model="form.notify_on_generation" inset label="Enviar aviso ao gerar" color="primary" />
      </div>

      <v-row dense>
        <v-col cols="12" md="4">
          <v-text-field
            v-model.number="form.generation_day"
            label="Dia de geração"
            type="number"
            min="1"
            max="28"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="form.reference_month_strategy"
            :items="referenceStrategies"
            label="Competência"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model.number="form.default_due_day"
            label="Vencimento padrão"
            type="number"
            min="1"
            max="28"
            outlined
            dense
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <v-switch
        v-model="form.auto_issue_gateway_payment"
        inset
        label="Emitir fatura automaticamente"
        color="primary"
        class="mt-2"
      />
    </section>

    <v-dialog v-model="planPaymentDialog" max-width="620">
      <v-card class="platform-payment-dialog">
        <v-card-title>
          <div>
            <span>Contratar {{ selectedPlan ? selectedPlan.name : "plano BOARUA" }}</span>
            <small>Escolha a forma de pagamento e confirme para gerar a cobrança.</small>
          </div>
          <v-btn icon @click="planPaymentDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="platform-payment-summary">
            <div>
              <span>Status</span>
              <strong>{{ planChargeStatusLabel }}</strong>
            </div>
            <div>
              <span>Valor</span>
              <strong>{{ money((activePlanCharge && activePlanCharge.amount) || planEstimate.total) }}</strong>
            </div>
            <div>
              <span>Vencimento</span>
              <strong>{{ formatDate(activePlanCharge && activePlanCharge.due_date) }}</strong>
            </div>
          </div>

          <div class="checkout-method-selector">
            <button
              v-for="method in boaruaPlanPaymentMethods"
              :key="method.value"
              type="button"
              :class="['checkout-method-option', { selected: form.boarua_plan_payment_method === method.value }]"
              @click="selectPlanPaymentMethod(method.value)"
            >
              <v-icon size="20">{{ method.icon }}</v-icon>
              <span>{{ method.text }}</span>
            </button>
          </div>

          <v-row v-if="form.boarua_plan_payment_method === 'credit_card'" dense class="checkout-card-fields">
            <v-col cols="12" md="6">
              <v-text-field v-model="planCreditCard.holderName" label="Nome impresso no cartão" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="planCreditCard.number" label="Número do cartão" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="planCreditCard.expiryMonth" label="Mês" maxlength="2" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="planCreditCard.expiryYear" label="Ano" maxlength="4" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="planCreditCard.ccv" label="CVV" maxlength="4" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field v-model="planCreditCard.document" label="CPF/CNPJ" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field v-model="planCreditCard.postalCode" label="CEP" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field v-model="planCreditCard.addressNumber" label="Número" outlined dense hide-details="auto" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="planCreditCard.phone" label="Telefone" outlined dense hide-details="auto" />
            </v-col>
          </v-row>

          <v-alert dense text type="info" class="mt-4 mb-0">
            Ao confirmar, o BOARUA gera a cobrança e apresenta os dados de pagamento nesta janela.
          </v-alert>

          <div v-if="selectedPlanCharge && planPaymentMethodValue === 'pix'" class="platform-payment-method-box">
            <div class="platform-payment-method-heading">
              <v-icon size="22">mdi-qrcode</v-icon>
              <div>
                <strong>Pix copia e cola</strong>
                <span>Copie o código abaixo para pagar no aplicativo do seu banco.</span>
              </div>
            </div>
            <img v-if="planPixQrImage" class="platform-payment-qr" :src="planPixQrImage" alt="QR Code Pix do plano BOARUA" />
            <textarea readonly :value="selectedPlanCharge && selectedPlanCharge.pix_copy_paste" placeholder="Código Pix ainda não disponível" />
            <v-btn depressed color="primary" :disabled="!selectedPlanCharge || !selectedPlanCharge.pix_copy_paste" @click="copyPlanPix">
              <v-icon left size="18">mdi-content-copy</v-icon>
              Copiar código Pix
            </v-btn>
          </div>

          <div v-else-if="selectedPlanCharge && planPaymentMethodValue === 'boleto'" class="platform-payment-method-box">
            <div class="platform-payment-method-heading">
              <v-icon size="22">mdi-barcode</v-icon>
              <div>
                <strong>Boleto da assinatura</strong>
                <span>Use a linha digitável abaixo para pagar a mensalidade do plano.</span>
              </div>
            </div>
            <textarea readonly :value="selectedPlanCharge && selectedPlanCharge.boleto_barcode" placeholder="Linha digitável ainda não disponível" />
            <v-btn depressed color="primary" :disabled="!selectedPlanCharge || !selectedPlanCharge.boleto_barcode" @click="copyPlanBoleto">
              <v-icon left size="18">mdi-content-copy</v-icon>
              Copiar código do boleto
            </v-btn>
          </div>

          <div v-else-if="selectedPlanCharge" class="platform-payment-method-box">
            <div class="platform-payment-method-heading">
              <v-icon size="22">mdi-credit-card-outline</v-icon>
              <div>
                <strong>Cartão de crédito</strong>
                <span>O cartão cadastrado será usado na recorrência mensal da assinatura.</span>
              </div>
            </div>
            <p class="platform-payment-note">
              Se o pagamento ainda estiver pendente, revise os dados do cartão no formulário do plano e gere a cobrança novamente.
            </p>
          </div>

          <div v-else-if="activePlanCharge && activePlanCharge.payment_method !== form.boarua_plan_payment_method" class="platform-payment-method-box">
            <div class="platform-payment-method-heading">
              <v-icon size="22">mdi-sync-alert</v-icon>
              <div>
                <strong>Gerar cobrança em {{ selectedPaymentMethodLabel }}</strong>
                <span>A cobrança atual foi gerada em {{ activePlanChargeMethodLabel }}. Confirme para cancelar a cobrança anterior e gerar os dados de {{ selectedPaymentMethodLabel }}.</span>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="planPaymentDialog = false">Fechar</v-btn>
          <v-btn depressed color="primary" :loading="savingPlan" :disabled="activePlanCharge && activePlanCharge.status === 'paid'" @click="saveBoaruaPlan">
            {{ planActionLabel }}
          </v-btn>
          <v-btn outlined color="primary" :loading="loadingPlanCharge" @click="refreshPlanCharge">
            <v-icon left size="18">mdi-refresh</v-icon>
            Atualizar status
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BoaruaPaymentSettings",
  data() {
    return {
      loading: false,
      saving: false,
      savingAccount: false,
      creatingSubaccount: false,
      connectingExistingAccount: false,
      savingPlan: false,
      loadingUnits: false,
      loadingPlanCharge: false,
      cepLoading: false,
      showAsaasApiKey: false,
      planPaymentDialog: false,
      association: null,
      activePlanCharge: null,
      planCreditCard: {
        holderName: "",
        number: "",
        expiryMonth: "",
        expiryYear: "",
        ccv: "",
        document: "",
        postalCode: "",
        addressNumber: "",
        phone: "",
      },
      unitCounts: {
        residences: 0,
        commerces: 0,
      },
      paymentAccount: {
        legal_name: "",
        trade_name: "",
        document: "",
        company_type: "",
        income_value: "",
        email: "",
        phone: "",
        address: "",
        address_number: "",
        complement: "",
        province: "",
        postal_code: "",
        city: "",
        state: "",
        payout_type: "pix",
        payout_pix_key_type: "",
        payout_pix_key: "",
        payout_bank_code: "",
        payout_bank_name: "",
        payout_agency: "",
        payout_agency_digit: "",
        payout_account: "",
        payout_account_digit: "",
        payout_account_type: "",
        payout_holder_name: "",
        payout_holder_document: "",
        gateway_wallet_id: "",
        gateway_api_key: "",
        status: "pending",
        onboarding_status: "pending",
        last_error: "",
      },
      form: {
        allow_pix: true,
        allow_boleto: true,
        allow_credit_card: true,
        allow_recurring_credit_card: false,
        auto_generate_charges: true,
        generation_day: null,
        reference_month_strategy: "current",
        default_due_day: null,
        notify_on_generation: true,
        auto_issue_gateway_payment: false,
        default_payment_method: "pix",
        boarua_billing_model: "plan",
        boarua_plan_key: "plus",
        boarua_plan_monthly_amount: 149,
        boarua_plan_payment_method: "pix",
        association_usage_type: "residential",
      },
      associationUsageTypes: [
        {
          value: "residential",
          title: "Residencial",
          description: "Associações, bairros, ruas, condomínios e loteamentos residenciais.",
          icon: "mdi-home-outline",
        },
        {
          value: "mixed",
          title: "Misto",
          description: "Locais com moradores, comércios, salas e serviços no mesmo fluxo.",
          icon: "mdi-home-city-outline",
        },
        {
          value: "commercial",
          title: "Comercial",
          description: "Prédios comerciais, galerias, salas e condomínios corporativos.",
          icon: "mdi-office-building-outline",
        },
        {
          value: "real_estate",
          title: "Imobiliária",
          description: "Carteiras de imóveis, salas locadas e administração patrimonial.",
          icon: "mdi-domain-outline",
        },
      ],
      boaruaPlans: [
        {
          key: "basic",
          name: "Basic",
          price: 129,
          promotionalPrice: 99,
          residencesIncluded: 50,
          commercesIncluded: 1,
          additionalResidence: 1.5,
          additionalCommerce: 14.9,
          icon: "mdi-home-outline",
          theme: "blue",
        },
        {
          key: "plus",
          name: "Plus",
          price: 199,
          promotionalPrice: 149,
          residencesIncluded: 150,
          commercesIncluded: 3,
          additionalResidence: 1.25,
          additionalCommerce: 14.9,
          icon: "mdi-account-group-outline",
          theme: "green",
          recommended: true,
        },
        {
          key: "pro",
          name: "Pro",
          price: 299,
          promotionalPrice: 249,
          residencesIncluded: 400,
          commercesIncluded: 6,
          additionalResidence: 1,
          additionalCommerce: 12.9,
          icon: "mdi-shield-star-outline",
          theme: "blue",
        },
        {
          key: "premium",
          name: "Premium",
          price: 499,
          promotionalPrice: 399,
          residencesIncluded: 1000,
          commercesIncluded: 10,
          additionalResidence: 0.8,
          additionalCommerce: 9.9,
          icon: "mdi-office-building-outline",
          theme: "green",
        },
        {
          key: "enterprise",
          name: "Enterprise",
          price: null,
          promotionalPrice: null,
          residencesIncluded: Infinity,
          commercesIncluded: Infinity,
          additionalResidence: null,
          additionalCommerce: null,
          icon: "mdi-crown-outline",
          theme: "purple",
        },
      ],
      boaruaPlanPaymentMethods: [
        {
          text: "PIX",
          value: "pix",
          icon: "mdi-qrcode",
          description: "Recorrência mensal com cobrança por Pix.",
        },
        {
          text: "Boleto",
          value: "boleto",
          icon: "mdi-barcode",
          description: "Recorrência mensal com boleto.",
        },
        {
          text: "Cartão de crédito",
          value: "credit_card",
          icon: "mdi-credit-card-outline",
          description: "Recorrência mensal no cartão da associação.",
        },
      ],
      referenceStrategies: [
        { text: "Mês atual", value: "current" },
        { text: "Próximo mês", value: "next" },
      ],
      companyTypes: [
        { text: "MEI", value: "MEI" },
        { text: "Limitada", value: "LIMITED" },
        { text: "Individual", value: "INDIVIDUAL" },
        { text: "Entidade", value: "ASSOCIATION" },
      ],
      payoutTypes: [
        { text: "Chave Pix", value: "pix" },
        { text: "Conta bancária", value: "bank_account" },
      ],
      pixKeyTypes: [
        { text: "CNPJ", value: "CNPJ" },
        { text: "CPF", value: "CPF" },
        { text: "E-mail", value: "EMAIL" },
        { text: "Telefone", value: "PHONE" },
        { text: "Chave aleatória", value: "EVP" },
      ],
      accountTypes: [
        { text: "Conta corrente", value: "CHECKING_ACCOUNT" },
        { text: "Conta poupança", value: "SAVINGS_ACCOUNT" },
      ],
    };
  },
  computed: {
    isAccountModule() {
      return this.$route && this.$route.path === "/configuracoes/conta";
    },
    isPlansModule() {
      return this.$route && this.$route.path === "/configuracoes/planos";
    },
    isPaymentSettingsModule() {
      return !this.isAccountModule && !this.isPlansModule;
    },
    pageTitle() {
      if (this.isAccountModule) return "Conta";
      if (this.isPlansModule) return "Planos";
      return "Pagamentos";
    },
    pageSubtitle() {
      if (this.isAccountModule) return "Resumo da assinatura e liberação da conta BOARUA.";
      if (this.isPlansModule) return "Escolha um plano e pague em uma janela segura.";
      return "Defina quais meios o local aceita nas cobranças.";
    },
    availableDefaultMethods() {
      const items = [];
      if (this.form.allow_pix) items.push({ text: "PIX", value: "pix" });
      if (this.form.allow_boleto) items.push({ text: "Boleto", value: "boleto" });
      if (this.form.allow_credit_card) items.push({ text: "Cartão de crédito", value: "credit_card" });
      return items;
    },
    paymentAccountStatus() {
      const status = String(this.paymentAccount.status || "");
      const onboardingStatus = String(this.paymentAccount.onboarding_status || "");
      if (this.paymentAccount.gateway_wallet_id) {
        return { label: "Split ativo", color: "primary" };
      }
      if (status === "active") {
        return { label: "Conta ativa", color: "primary" };
      }
      if (onboardingStatus === "requires_connection") {
        return { label: "Conectar conta existente", color: "warning" };
      }
      if (onboardingStatus === "connected") {
        return { label: "Conta conectada", color: "primary" };
      }
      if (onboardingStatus === "error") {
        return { label: "Erro", color: "error" };
      }
      return { label: "Pendente", color: "grey" };
    },
    paymentAccountErrorTooltip() {
      if (String(this.paymentAccount.onboarding_status || "") !== "error") return "";
      return this.paymentAccount.last_error || "Nao foi possivel ativar a conta de recebimento.";
    },
    requiresAsaasConnection() {
      return String(this.paymentAccount.onboarding_status || "") === "requires_connection";
    },
    paymentAccountExists() {
      return Boolean(this.paymentAccount && this.paymentAccount.id);
    },
    paymentAccountLocked() {
      return this.paymentAccountExists;
    },
    showPaymentAccountActions() {
      return this.requiresAsaasConnection || !this.paymentAccountExists;
    },
    formattedPaymentDocument() {
      const digits = String(this.paymentAccount.document || "").replace(/\D/g, "");
      if (digits.length !== 14) return this.paymentAccount.document || "";
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    },
    selectedPlan() {
      return this.boaruaPlans.find((plan) => plan.key === this.form.boarua_plan_key) || this.boaruaPlans[1];
    },
    visibleBoaruaPlans() {
      return this.boaruaPlans.filter((plan) => plan.key !== "enterprise");
    },
    planAccessStatus() {
      return (this.association && this.association.access_status) || {};
    },
    associationProfileLocked() {
      return Boolean(this.association && this.association.id);
    },
    planStatusAlert() {
      const status = this.planAccessStatus || {};
      const state = String(status.state || "");
      const reason = String(status.reason || "");

      if (state === "trial") {
        return {
          type: "info",
          message: status.message || "Trial de 7 dias ativo. Contrate um plano para manter o acesso apos o periodo gratuito.",
        };
      }

      if (reason === "trial_expired") {
        return {
          type: "warning",
          message: status.message || "O trial terminou. Contrate um plano para liberar residencias, administracao e uso dos moradores.",
        };
      }

      if (state === "past_due_grace") {
        return {
          type: "warning",
          message: status.message || "Pagamento em atraso. A plataforma ficará liberada durante o período de carência.",
        };
      }

      if (state === "blocked") {
        return {
          type: "error",
          message: status.message || "Associacao bloqueada. Regularize o plano para liberar a plataforma.",
        };
      }

      if (this.association && this.association.boarua_plan_contracted_at) {
        return {
          type: "success",
          message: "Plano BOARUA contratado e associação liberada.",
        };
      }

      return null;
    },
    planActionLabel() {
      const status = this.planAccessStatus || {};
      if (this.activePlanCharge && this.activePlanCharge.status === "pending") return "Atualizar cobrança";
      if (this.activePlanCharge && this.activePlanCharge.status === "paid") return "Plano pago";
      if (status.reason === "trial_expired" || status.state === "blocked") return "Gerar cobrança";
      if (status.state === "trial") return "Contratar plano";
      return "Gerar cobrança";
    },
    planChargeStatusLabel() {
      const charge = this.activePlanCharge;
      if (!charge) return "Nenhuma cobrança gerada";
      const labels = {
        pending: "Aguardando pagamento",
        paid: "Pago",
        overdue: "Vencido",
        cancelled: "Cancelado",
        refunded: "Estornado",
      };
      return labels[charge.status] || charge.status || "Aguardando pagamento";
    },
    planPaymentMethodValue() {
      return this.form.boarua_plan_payment_method || (this.activePlanCharge && this.activePlanCharge.payment_method) || "pix";
    },
    selectedPlanCharge() {
      if (!this.activePlanCharge) return null;
      return this.activePlanCharge.payment_method === this.planPaymentMethodValue ? this.activePlanCharge : null;
    },
    selectedPaymentMethodLabel() {
      return this.paymentMethodLabel(this.planPaymentMethodValue);
    },
    activePlanChargeMethodLabel() {
      return this.paymentMethodLabel(this.activePlanCharge && this.activePlanCharge.payment_method);
    },
    planPixQrImage() {
      const value = this.selectedPlanCharge && this.selectedPlanCharge.pix_qr_code;
      if (!value) return "";
      if (String(value).startsWith("data:image")) return value;
      return `data:image/png;base64,${value}`;
    },
    planEstimate() {
      const plan = this.selectedPlan;
      if (!plan || plan.price === null) {
        return {
          base: 0,
          extraResidences: 0,
          extraCommerces: 0,
          extraResidenceAmount: 0,
          extraCommerceAmount: 0,
          total: 0,
        };
      }

      const extraResidences = Math.max(0, this.unitCounts.residences - plan.residencesIncluded);
      const extraCommerces = Math.max(0, this.unitCounts.commerces - plan.commercesIncluded);
      const extraResidenceAmount = extraResidences * plan.additionalResidence;
      const extraCommerceAmount = extraCommerces * plan.additionalCommerce;
      const basePrice = this.planEffectivePrice(plan);

      return {
        base: basePrice,
        extraResidences,
        extraCommerces,
        extraResidenceAmount,
        extraCommerceAmount,
        total: basePrice + extraResidenceAmount + extraCommerceAmount,
      };
    },
  },
  watch: {
    availableDefaultMethods(items) {
      if (!items.find((item) => item.value === this.form.default_payment_method)) {
        this.form.default_payment_method = items[0] ? items[0].value : null;
      }
      if (!this.form.allow_credit_card) {
        this.form.allow_recurring_credit_card = false;
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const response = await axios.get("/association-payment-settings");
        this.form = Object.assign({}, this.form, response.data.content || {});
        this.form.auto_generate_charges = true;
        await Promise.all([
          this.loadPaymentAccount(),
          this.loadAssociation(),
          this.loadUnitCounts(),
          this.loadPlanCharge(),
        ]);
      } finally {
        this.loading = false;
      }
    },
    async loadAssociation() {
      const response = await axios.get("/associations/current/profile");
      this.association = response.data.content || null;
      if (!this.association) return;

      this.form = Object.assign({}, this.form, {
        boarua_billing_model: this.association.boarua_billing_model || "plan",
        boarua_plan_key: this.association.boarua_plan_key || this.form.boarua_plan_key || "plus",
        boarua_plan_monthly_amount: Number(this.association.boarua_plan_monthly_amount || this.form.boarua_plan_monthly_amount || 149),
        boarua_plan_payment_method: this.association.boarua_plan_payment_method || this.form.boarua_plan_payment_method || "pix",
        association_usage_type: this.association.association_usage_type || this.form.association_usage_type || "residential",
      });
    },
    async loadUnitCounts() {
      this.loadingUnits = true;
      try {
        const response = await axios.get("/member-units", { params: { limit: 1000, search: JSON.stringify({ status: "active" }) } });
        const units = response.data.content.data || response.data.content || [];
        this.unitCounts = units.reduce((counts, unit) => {
          if (this.isCommerceUnit(unit)) counts.commerces += 1;
          else counts.residences += 1;
          return counts;
        }, { residences: 0, commerces: 0 });
      } catch (error) {
        this.unitCounts = { residences: 0, commerces: 0 };
      } finally {
        this.loadingUnits = false;
      }
    },
    async loadPaymentAccount() {
      const response = await axios.get("/payment-accounts/current");
      this.paymentAccount = Object.assign({}, this.paymentAccount, response.data.content || {}, {
        gateway_api_key: "",
      });
    },
    async loadPlanCharge() {
      this.loadingPlanCharge = true;
      try {
        const response = await axios.get("/boarua-plan-charges/current");
        const content = response.data.content || {};
        this.activePlanCharge = content.charge || null;
      } catch (error) {
        this.activePlanCharge = null;
      } finally {
        this.loadingPlanCharge = false;
      }
    },
    async loadPaymentAddressByCep() {
      if (this.paymentAccountLocked) return;

      const cep = String(this.paymentAccount.postal_code || "").replace(/\D/g, "");

      if (cep.length !== 8) return;

      this.cepLoading = true;

      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        if (!response.ok || data.erro) {
          this.notifyError("CEP não encontrado.");
          return;
        }

        this.paymentAccount.address = data.logradouro || this.paymentAccount.address;
        this.paymentAccount.province = data.bairro || this.paymentAccount.province;
        this.paymentAccount.city = data.localidade || this.paymentAccount.city;
        this.paymentAccount.state = data.uf || this.paymentAccount.state;
      } catch (error) {
        this.notifyError("Não foi possível consultar o CEP.");
      } finally {
        this.cepLoading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        this.form.auto_generate_charges = true;
        const paymentPayload = Object.assign({}, this.form);
        delete paymentPayload.boarua_billing_model;
        delete paymentPayload.boarua_plan_key;
        delete paymentPayload.boarua_plan_monthly_amount;
        delete paymentPayload.boarua_plan_payment_method;
        delete paymentPayload.association_usage_type;
        await axios.patch("/association-payment-settings", paymentPayload);
        this.notifySuccess("Configuração salva");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : error.message || "Não foi possível salvar");
      } finally {
        this.saving = false;
      }
    },
    selectPlan(planKey) {
      this.form.boarua_plan_key = planKey;
    },
    selectPlanPaymentMethod(method) {
      this.form.boarua_plan_payment_method = method;
    },
    paymentMethodLabel(method) {
      const item = this.boaruaPlanPaymentMethods.find((option) => option.value === method);
      return item ? item.text : "pagamento";
    },
    openPlanCheckout(planKey) {
      this.selectPlan(planKey);
      this.planPaymentDialog = true;
    },
    planSummary(plan) {
      if (!plan) return "";
      if (plan.key === "basic") return "Para locais menores começarem com cobrança e gestão.";
      if (plan.key === "plus") return "Para associações em crescimento com operação recorrente.";
      if (plan.key === "pro") return "Para locais maiores com mais unidades e comércios.";
      if (plan.key === "premium") return "Para operações robustas com alto volume de moradores.";
      return "Para operações sob medida.";
    },
    selectAssociationUsageType(usageType) {
      if (this.associationProfileLocked) return;
      this.form.association_usage_type = usageType;
    },
    async saveBoaruaPlan() {
      if (this.activePlanCharge && this.activePlanCharge.status === "paid") {
        this.notifySuccess("Plano BOARUA já está pago");
        return;
      }

      this.savingPlan = true;
      try {
        const estimate = this.planEstimate;
        const usageType = this.associationProfileLocked
          ? (this.association.association_usage_type || "residential")
          : (this.form.association_usage_type || "residential");
        const payload = {
          boarua_billing_model: "plan",
          boarua_plan_key: this.form.boarua_plan_key || "plus",
          boarua_plan_monthly_amount: estimate.total,
          boarua_plan_payment_method: this.form.boarua_plan_payment_method || "pix",
          association_usage_type: usageType,
          boarua_plan_snapshot: JSON.stringify({
            plan: this.selectedPlan,
            usageType,
            paymentMethod: this.form.boarua_plan_payment_method || "pix",
            usage: this.unitCounts,
            estimate,
            note: "Tarifas bancarias de boleto, Pix e cartao cobradas separadamente.",
          }),
          credit_card: this.form.boarua_plan_payment_method === "credit_card" ? this.buildPlanCreditCardPayload() : null,
        };

        const response = await axios.post("/boarua-plan-charges/checkout", payload);
        const content = response.data.content || {};
        this.activePlanCharge = content.charge || null;
        this.form.boarua_billing_model = "plan";
        this.form.boarua_plan_monthly_amount = estimate.total;
        this.form.boarua_plan_payment_method = payload.boarua_plan_payment_method;
        await this.loadAssociation();
        this.notifySuccess(this.activePlanCharge && this.activePlanCharge.status === "paid" ? "Pagamento confirmado" : "Cobrança do plano gerada");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível gerar a cobrança do plano");
      } finally {
        this.savingPlan = false;
      }
    },
    async refreshPlanCharge() {
      await this.loadPlanCharge();
      await this.loadAssociation();
      this.notifySuccess("Status do plano atualizado");
    },
    buildPlanCreditCardPayload() {
      return {
        holderName: this.planCreditCard.holderName,
        number: this.onlyDigits(this.planCreditCard.number),
        expiryMonth: this.planCreditCard.expiryMonth,
        expiryYear: this.planCreditCard.expiryYear,
        ccv: this.planCreditCard.ccv,
        document: this.onlyDigits(this.planCreditCard.document),
        postalCode: this.onlyDigits(this.planCreditCard.postalCode),
        addressNumber: this.planCreditCard.addressNumber,
        phone: this.onlyDigits(this.planCreditCard.phone),
      };
    },
    async savePaymentAccount() {
      this.savingAccount = true;
      try {
        const response = await axios.patch("/payment-accounts/current", this.buildPaymentAccountPayload());
        this.paymentAccount = Object.assign({}, this.paymentAccount, response.data.content || {});
        this.notifySuccess("Conta de pagamento salva");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível salvar a conta de pagamento");
      } finally {
        this.savingAccount = false;
      }
    },
    async createAsaasSubaccount() {
      this.creatingSubaccount = true;
      try {
        const response = await axios.post("/payment-accounts/current/asaas-subaccount", this.buildPaymentAccountPayload());
        this.paymentAccount = Object.assign({}, this.paymentAccount, response.data.content || {}, {
          gateway_api_key: "",
        });
        if (this.requiresAsaasConnection) {
          this.notifySuccess("Local cadastrado. Vincule a conta de recebimento existente para ativar as cobranças.");
        } else {
          this.notifySuccess("Conta de recebimento criada e repasse automático ativado");
        }
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível criar a conta de recebimento");
        await this.loadPaymentAccount();
      } finally {
        this.creatingSubaccount = false;
      }
    },
    async connectExistingAsaas() {
      this.connectingExistingAccount = true;
      try {
        const response = await axios.post("/payment-accounts/current/connect-existing-asaas", this.buildPaymentAccountPayload());
        this.paymentAccount = Object.assign({}, this.paymentAccount, response.data.content || {}, {
          gateway_api_key: "",
        });
        this.notifySuccess("Conta de recebimento vinculada e repasse automático ativado");
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : "Não foi possível vincular a conta de recebimento");
      } finally {
        this.connectingExistingAccount = false;
      }
    },
    notifySuccess(message) {
      if (this.$dialog && this.$dialog.notify) {
        this.$dialog.notify.success(message, { position: "top-right", timeout: 3000 });
      }
    },
    notifyError(message) {
      if (this.$dialog && this.$dialog.notify) {
        this.$dialog.notify.error(message, { position: "top-right", timeout: 4000 });
      }
    },
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    isCommerceUnit(unit) {
      const type = String(unit.address_type || unit.type || unit.category || unit.unit_type || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      return ["commerce", "commercial", "comercio", "comercial", "store", "business"].includes(type);
    },
    money(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    planResidencesLabel(plan) {
      if (plan.key === "enterprise") return "acima de 1.000";
      return `até ${Number(plan.residencesIncluded).toLocaleString("pt-BR")}`;
    },
    planCommercesLabel(plan) {
      if (plan.key === "enterprise") return "negociado";
      return Number(plan.commercesIncluded).toLocaleString("pt-BR");
    },
    planAdditionalResidenceLabel(plan) {
      return plan.additionalResidence === null ? "negociado" : `${this.money(plan.additionalResidence)}/mês`;
    },
    planAdditionalCommerceLabel(plan) {
      return plan.additionalCommerce === null ? "negociado" : `${this.money(plan.additionalCommerce)}/mês`;
    },
    planEffectivePrice(plan) {
      if (!plan || plan.price === null) return 0;
      return plan.promotionalPrice !== null && plan.promotionalPrice !== undefined ? Number(plan.promotionalPrice) : Number(plan.price || 0);
    },
    formatDate(value) {
      if (!value) return "-";
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[3]}/${match[2]}/${match[1]}`;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    async copyPlanPix() {
      await this.copyText(this.selectedPlanCharge && this.selectedPlanCharge.pix_copy_paste);
      this.notifySuccess("Código Pix copiado");
    },
    async copyPlanBoleto() {
      await this.copyText(this.selectedPlanCharge && this.selectedPlanCharge.boleto_barcode);
      this.notifySuccess("Código do boleto copiado");
    },
    async copyText(value) {
      if (!value) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      }
    },
    buildPaymentAccountPayload() {
      return Object.assign({}, this.paymentAccount, {
        document: this.onlyDigits(this.paymentAccount.document),
        phone: this.onlyDigits(this.paymentAccount.phone),
        postal_code: this.onlyDigits(this.paymentAccount.postal_code),
        payout_holder_document: this.onlyDigits(this.paymentAccount.payout_holder_document),
      });
    },
  },
};
</script>

<style scoped lang="scss">
.resource-toolbar {
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 20px;
  }

  p {
    margin: 3px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.settings-panel {
  width: 100%;
  max-width: none;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
}

.account-flow {
  display: grid;
  gap: 16px;
  width: 100%;
  max-width: none;
}

.account-status-panel {
  padding: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border-color: #f3c38d;
  background: #fffaf3;
}

.account-status-copy {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 14px;

  h3 {
    margin: 0;
    color: #7c2d12;
    font-size: 22px;
    font-weight: 900;
    line-height: 1.15;
  }

  p {
    max-width: 620px;
    margin: 6px 0 0;
    color: #9a3412;
    font-size: 13px;
    line-height: 1.45;
  }
}

.account-status-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 10px;
  background: #ffedd5;
  color: #c2410c;

  .v-icon {
    color: currentColor;
  }
}

.account-status-total {
  min-width: 210px;
  padding: 14px 16px;
  border: 1px solid #b7e1c9;
  border-radius: 8px;
  background: #f3fbf6;
  text-align: right;

  span,
  strong {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 12px;
    font-weight: 800;
  }

  strong {
    margin-top: 2px;
    color: #04783b;
    font-size: 24px;
    font-weight: 900;
    line-height: 1.1;
  }
}

.account-step-panel {
  padding: 22px;
}

.account-step-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;

  > div {
    min-width: 0;
    flex: 1;
  }

  h3 {
    margin: 0;
    color: #10223f;
    font-size: 19px;
    font-weight: 900;
    line-height: 1.2;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
    line-height: 1.45;
  }
}

.account-usage-summary,
.account-final-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;

  div {
    padding: 13px 14px;
    border: 1px solid #dfe8e3;
    border-radius: 8px;
    background: #fbfdfc;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 11px;
    font-weight: 900;
    line-height: 1.25;
    text-transform: uppercase;
  }

  strong {
    margin-top: 5px;
    color: #10223f;
    font-size: 22px;
    font-weight: 900;
    line-height: 1.1;
  }
}

.account-plan-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.account-plan-card {
  position: relative;
  min-height: 178px;
  padding: 16px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;
  color: #10223f;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;

  &.selected {
    border-color: #04783b;
    box-shadow: 0 10px 24px rgba(4, 120, 59, 0.12);
    transform: translateY(-1px);
  }

  strong {
    color: #10223f;
    font-size: 18px;
    font-weight: 900;
    line-height: 1.1;
  }

  small {
    color: #66736b;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.35;
  }

  em {
    margin-top: auto;
    color: #04783b;
    font-size: 12px;
    font-style: normal;
    font-weight: 900;
  }
}

.account-plan-price {
  display: block;
  color: #10223f;
  font-size: 22px;
  font-weight: 900;
  line-height: 1;

  small {
    margin-left: 2px;
    color: #66736b;
    font-size: 11px;
  }

  &.custom {
    font-size: 17px;
  }
}

.account-plan-badge {
  width: max-content;
  padding: 4px 8px;
  border-radius: 999px;
  background: #eaf8f0;
  color: #04783b;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
}

.account-payment-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.account-final-panel {
  border-color: #b7e1c9;
  background: #fbfdfc;
}

.account-primary-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.plans-page {
  padding: 8px 0 12px;
}

.plans-hero {
  margin: 0 auto 30px;
  max-width: 680px;
  text-align: center;

  h2 {
    margin: 0;
    color: #10223f;
    font-size: clamp(30px, 4vw, 44px);
    font-weight: 900;
    line-height: 1.1;
  }

  p {
    margin: 12px 0 0;
    color: #66736b;
    font-size: 15px;
    line-height: 1.5;
  }
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: stretch;
  gap: 14px;
  max-width: 1080px;
  margin: 0 auto;
}

.pricing-choice-card {
  min-height: 500px;
  padding: 34px 28px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border: 1px solid #e5ebe7;
  background: #ffffff;
  color: #10223f;
  text-align: left;
  cursor: pointer;
  position: relative;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

  border-radius: 18px;

  &.selected {
    border-color: #8dc9aa;
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
  }

  &:hover {
    transform: translateY(-3px);
  }

.pricing-choice-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px;

  strong {
    display: block;
    color: currentColor;
    font-size: 26px;
    font-weight: 900;
    line-height: 1.1;
  }
}

  p {
    min-height: 58px;
    margin: 14px 0 18px;
    color: #66736b;
    font-size: 13px;
    line-height: 1.45;
  }

  em {
    min-height: 42px;
    margin-top: auto;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: #eef4f1;
    color: #057a3d;
    font-size: 13px;
    font-style: normal;
    font-weight: 900;
  }
}

.pricing-choice-badge {
  width: max-content;
  margin-left: auto;
  padding: 6px 12px;
  border-radius: 999px;
  background: #eaf8f0;
  color: #057a3d;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pricing-choice-price {
  display: block;
  margin-top: 24px;
  color: currentColor;
  font-size: 34px;
  font-weight: 900;
  line-height: 1;

  small {
    margin-left: 4px;
    color: #66736b;
    font-size: 13px;
    font-weight: 700;
  }

  &.custom {
    font-size: 22px;
  }
}

.pricing-choice-lines {
  display: grid;
  gap: 12px;
  margin: 8px 0 26px;

  span {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #4e5d55;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.35;
  }

  .v-icon {
    color: #057a3d;
  }
}

.checkout-method-selector {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.checkout-method-option {
  min-height: 48px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;
  color: #4e5d55;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;

  .v-icon {
    color: currentColor;
  }

  &.selected {
    border-color: #057a3d;
    background: #eaf8f0;
    color: #057a3d;
  }
}

.checkout-card-fields {
  margin-top: 12px;
}

.account-settings-shell {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 26px;
  align-items: start;
}

.account-settings-nav {
  position: sticky;
  top: 18px;
  padding: 8px;
  border: 1px solid #e1e7e0;
  border-radius: 8px;
  background: #ffffff;
}

.account-nav-item {
  width: 100%;
  min-height: 42px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #48554f;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;

  .v-icon {
    color: currentColor;
  }

  &.active,
  &:hover {
    color: #10223f;
    background: #eef4f1;
  }
}

.account-content-panel {
  min-height: 640px;
}

.account-general-panel {
  display: grid;
  gap: 16px;
}

.account-hero-card {
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #fbfdfc;

  h4 {
    margin: 0;
    color: #10223f;
    font-size: 18px;
    font-weight: 900;
  }

  p {
    margin: 6px 0 0;
    color: #66736b;
    font-size: 13px;
    line-height: 1.45;
  }
}

.account-hero-icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 8px;
  background: #eaf8f0;
  color: #04783b;

  .v-icon {
    color: currentColor;
  }
}

.account-empty-panel {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #66736b;
  text-align: center;

  .v-icon {
    color: #04783b;
  }

  h4 {
    margin: 6px 0 0;
    color: #10223f;
    font-size: 18px;
    font-weight: 900;
  }

  p {
    max-width: 460px;
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
  }
}

.payment-methods-panel {
  max-width: none;
}

.pricing-panel {
  max-width: none;
  overflow: visible;
}

.panel-heading {
  margin-bottom: 12px;

  h3 {
    margin: 0;
    color: #111827;
    font-size: 18px;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 13px;
  }
}

.form-section-title {
  margin-top: 6px;
  padding-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #057a3d;
  border-top: 1px solid #e6ece8;
  font-size: 13px;
  font-weight: 800;

  .v-icon {
    color: #057a3d;
  }
}

.payment-account-heading,
.payment-account-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.payment-account-actions {
  margin-top: 18px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.pricing-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.pricing-summary {
  min-width: 190px;
  padding: 14px 16px;
  border: 1px solid #b7e1c9;
  border-radius: 8px;
  background: #f3fbf6;
  text-align: right;

  span {
    display: block;
    color: #66736b;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin-top: 2px;
    color: #04783b;
    font-size: 24px;
    line-height: 1.1;
  }
}

.payment-method-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.payment-method-option {
  min-height: 72px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #f8fbf9;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &.active {
    border-color: #8dc9aa;
    background: #f0faf5;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.66;
  }

  ::v-deep .v-input--switch {
    margin: 0;
  }

  ::v-deep .v-label {
    color: #10223f;
    font-size: 13px;
    font-weight: 800;
  }
}

.payment-default-select {
  margin-top: 32px !important;
  margin-bottom: 10px;

  ::v-deep .v-input__slot {
    margin-bottom: 0;
  }
}

.usage-strip,
.plan-estimate {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 16px 0;

  div {
    padding: 12px 14px;
    border: 1px solid #dfe8e3;
    border-radius: 8px;
    background: #f8fbf9;
  }

  span {
    display: block;
    color: #66736b;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: #10223f;
    font-size: 18px;
  }
}

.contract-profile {
  margin: 18px 0;
  padding: 16px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #fbfdfc;
}

.contract-profile-heading {
  margin-bottom: 12px;

  h4 {
    margin: 0;
    color: #10223f;
    font-size: 15px;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 12px;
    line-height: 1.45;
  }
}

.contract-profile-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.contract-profile-option {
  min-height: 140px;
  padding: 14px;
  border: 1px solid #dbe6e1;
  border-radius: 8px;
  background: #ffffff;
  color: #10223f;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &.selected {
    border-color: #04783b;
    box-shadow: 0 10px 26px rgba(4, 120, 59, 0.12);
    transform: translateY(-1px);

    .contract-profile-icon {
      background: #eaf8f0;
      color: #04783b;
    }
  }

  &.locked {
    cursor: not-allowed;
    opacity: 0.58;
    pointer-events: none;
  }

  &.locked.selected {
    opacity: 1;
    transform: none;
    box-shadow: none;
  }

  strong,
  small {
    display: block;
  }

  strong {
    margin-top: 10px;
    font-size: 14px;
    line-height: 1.2;
  }

  small {
    margin-top: 6px;
    color: #66736b;
    font-size: 11px;
    line-height: 1.35;
  }
}

.contract-profile-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #0f4f99;

  .v-icon {
    color: currentColor;
  }
}

.plan-payment-method {
  margin: 18px 0;
  padding: 16px;
  display: grid;
  grid-template-columns: minmax(240px, 0.9fr) minmax(0, 2.1fr);
  gap: 16px;
  align-items: center;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;
}

.plan-payment-heading {
  margin: 0;

  h4 {
    margin: 0;
    color: #10223f;
    font-size: 15px;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 12px;
    line-height: 1.45;
  }
}

.plan-payment-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.plan-payment-option {
  min-height: 64px;
  padding: 10px 12px;
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 2px 10px;
  align-items: center;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;
  color: #10223f;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &.selected {
    border-color: #04783b;
    background: #fbfdfc;
    box-shadow: 0 10px 24px rgba(4, 120, 59, 0.08);

    .plan-payment-icon {
      color: #04783b;
      background: #eaf8f0;
      border-color: #cfe9da;
    }
  }

  strong,
  small {
    display: block;
  }

  strong {
    font-size: 13px;
    font-weight: 900;
    line-height: 1.2;
  }

  small {
    grid-column: 2;
    color: #66736b;
    font-size: 10px;
    line-height: 1.25;
  }
}

.plan-payment-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 1px solid #e0e8e3;
  border-radius: 8px;
  background: #f7faf8;
  color: #60706a;

  .v-icon {
    color: currentColor;
  }
}

.plan-checkout-panel {
  margin: -6px 0 18px;
  padding: 16px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #fbfdfc;
}

.plan-checkout-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  div {
    padding: 12px 14px;
    border: 1px solid #e1e9e4;
    border-radius: 8px;
    background: #ffffff;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 11px;
    font-weight: 800;
  }

  strong {
    margin-top: 4px;
    color: #10223f;
    font-size: 15px;
  }
}

.plan-card-fields {
  margin-top: 12px;
}

.plan-payment-result {
  margin-top: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.plan-payment-link,
.plan-copy-button,
.plan-paid-state {
  min-height: 34px;
  padding: 7px 10px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #dfe8e3;
  border-radius: 6px;
  background: #ffffff;
  color: #04783b;
  font-size: 12px;
  font-weight: 900;
  text-decoration: none;
  cursor: pointer;
}

.plan-copy-button {
  cursor: pointer;
}

.plan-paid-state {
  border-color: #bde4cc;
  background: #eefaf3;
}

.platform-payment-dialog {
  border-radius: 8px;

  .v-card__title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 22px 14px;

    span,
    small {
      display: block;
    }

    span {
      color: #10223f;
      font-size: 20px;
      font-weight: 900;
      line-height: 1.2;
    }

    small {
      margin-top: 4px;
      color: #66736b;
      font-size: 13px;
      font-weight: 600;
    }
  }

  .v-card__text {
    padding: 0 22px 18px;
  }

  .v-card__actions {
    padding: 14px 22px 20px;
  }
}

.platform-payment-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  div {
    padding: 12px 14px;
    border: 1px solid #dfe8e3;
    border-radius: 8px;
    background: #fbfdfc;
  }

  span,
  strong {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  strong {
    margin-top: 5px;
    color: #10223f;
    font-size: 15px;
  }
}

.platform-payment-method-box {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;

  textarea {
    width: 100%;
    min-height: 92px;
    margin: 14px 0 10px;
    padding: 12px;
    border: 1px solid #d1ded7;
    border-radius: 8px;
    background: #fbfdfc;
    color: #10223f;
    font-size: 12px;
    line-height: 1.45;
    resize: vertical;
  }
}

.platform-payment-method-heading {
  display: flex;
  align-items: center;
  gap: 10px;

  .v-icon {
    color: #04783b;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #10223f;
    font-size: 15px;
    font-weight: 900;
  }

  span {
    margin-top: 2px;
    color: #66736b;
    font-size: 12px;
    font-weight: 600;
  }
}

.platform-payment-qr {
  display: block;
  width: 168px;
  max-width: 100%;
  margin: 14px auto 0;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;
}

.platform-payment-note {
  margin: 14px 0 0;
  color: #4e5d55;
  font-size: 13px;
  line-height: 1.45;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(170px, 1fr));
  gap: 14px;
}

.plan-card {
  --plan-accent: #04783b;
  --plan-accent-soft: #f7faf8;
  --plan-selected-shadow: rgba(15, 23, 42, 0.08);
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 318px;
  padding: 18px 16px 16px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;
  color: #10223f;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &.selected {
    border-color: var(--plan-accent);
    background: #fbfdfc;
    box-shadow: 0 12px 30px var(--plan-selected-shadow);
    transform: translateY(-1px);

    .plan-selected-indicator {
      opacity: 1;
      transform: scale(1);
    }

    .plan-icon {
      background: #eaf8f0;
      border-color: #cfe9da;
      color: #04783b;
    }
  }
}

.recommended-badge {
  position: absolute;
  top: -12px;
  left: 18px;
  padding: 4px 10px;
  border: 1px solid #04783b;
  border-radius: 5px;
  background: #ffffff;
  color: #04783b;
  font-size: 11px;
  font-weight: 900;
  text-align: center;
  text-transform: uppercase;
  box-shadow: 0 8px 18px rgba(4, 120, 59, 0.08);
}

.plan-selected-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #04783b;
  color: #ffffff;
  opacity: 0;
  transform: scale(0.82);
  transition: opacity 0.2s ease, transform 0.2s ease;

  .v-icon {
    color: #ffffff;
  }
}

.plan-top {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 62px;
}

.plan-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f8fbf9;
  color: #66736b;
  border: 1px solid #dce8e1;

  .v-icon {
    color: currentColor;
  }
}

.plan-name {
  display: block;
  color: #10223f;
  font-size: 22px;
  line-height: 1.1;
}

.plan-price-wrap {
  display: block;
  min-height: 96px;
  margin: 12px 0 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid #edf1ef;
}

.plan-price,
.plan-custom {
  display: block;
  color: #10223f;
  font-size: 34px;
  font-weight: 900;
  line-height: 1;
  text-align: left;

  small,
  em {
    color: #66736b;
    font-size: 13px;
    font-style: normal;
  }
}

.plan-custom {
  padding-top: 8px;
  font-size: 20px;
}

.plan-promo-price {
  display: block;
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid #dcebdd;
  border-radius: 8px;
  background: #f4faf5;
  color: #4e5d55;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.3;

  strong {
    color: #04783b;
    font-size: 12px;
    font-weight: 900;
    white-space: nowrap;
  }
}

.plan-lines {
  display: block;
  margin-top: auto;
}

.plan-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 38px;
  padding: 7px 0;
  border-bottom: 1px solid #edf1ef;
  color: #4e5d55;
  font-size: 12px;

  strong {
    color: #10223f;
    font-size: 13px;
    white-space: nowrap;
  }
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 24px;
}

.section-kicker {
  display: inline-block;
  margin-bottom: 6px;
  color: #04783b;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.split-heading {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 360px);
  gap: 16px;
  align-items: end;
  margin-bottom: 18px;
}

.split-heading .payment-default-select,
.payment-default-select {
  margin-top: 0 !important;
  margin-bottom: 0;
}

.payment-methods-panel,
.pricing-panel {
  padding: 26px;
}

.payment-method-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.payment-method-option {
  min-height: 112px;
  padding: 16px;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;

  > span:nth-child(2) {
    min-width: 0;
  }

  strong,
  small {
    display: block;
  }

  strong {
    color: #10223f;
    font-size: 14px;
    font-weight: 900;
    line-height: 1.2;
  }

  small {
    margin-top: 4px;
    color: #66736b;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
  }
}

.method-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border: 1px solid #dce8e1;
  border-radius: 8px;
  background: #ffffff;
  color: #64736c;

  .v-icon {
    color: currentColor;
  }
}

.payment-method-option.active .method-icon {
  border-color: #ccead8;
  background: #eaf8f0;
  color: #04783b;
}

.pricing-heading {
  margin-bottom: 18px;
}

.plan-status-alert {
  margin: 0 0 16px;
}

.plan-overview-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.overview-card {
  min-height: 104px;
  padding: 16px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #fbfdfc;

  span,
  strong,
  small {
    display: block;
  }

  span {
    color: #66736b;
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }

  strong {
    margin-top: 8px;
    color: #10223f;
    font-size: 24px;
    line-height: 1.1;
  }

  small {
    margin-top: 6px;
    color: #66736b;
    font-size: 12px;
    font-weight: 700;
  }

  &.highlight {
    border-color: #bde4cc;
    background: #f3fbf6;
  }
}

.settings-step {
  margin: 16px 0;
  padding: 16px;
  border: 1px solid #dfe8e3;
  border-radius: 8px;
  background: #ffffff;
}

.step-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;

  h4 {
    margin: 0;
    color: #10223f;
    font-size: 16px;
    line-height: 1.25;
  }

  p {
    margin: 4px 0 0;
    color: #66736b;
    font-size: 12px;
    line-height: 1.45;
  }
}

.step-number {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #04783b;
  color: #ffffff;
  font-size: 13px;
  font-weight: 900;
}

.contract-profile {
  background: #fbfdfc;
}

.contract-profile-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.contract-profile-option {
  min-height: 126px;
}

.plan-payment-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.plan-payment-option {
  min-height: 88px;
}

.plan-checkout-panel {
  margin: 16px 0;
  padding: 16px;
  background: #fbfdfc;
}

.checkout-heading {
  margin-bottom: 12px;
}

.plan-payment-result {
  padding-top: 12px;
  border-top: 1px solid #e5ede8;
}

.pricing-grid {
  margin-top: 18px;
}

.plan-card {
  min-height: 300px;
  padding: 18px;
}

.plan-estimate {
  margin: 14px 0 0;
}

@media (max-width: 720px) {
  .account-status-panel,
  .account-step-head {
    align-items: stretch;
    flex-direction: column;
  }

  .account-status-total {
    width: 100%;
    min-width: 0;
    text-align: left;
  }

  .account-plan-grid,
  .account-usage-summary,
  .account-final-summary,
  .plans-grid,
  .checkout-method-selector {
    grid-template-columns: 1fr;
  }

  .plans-page {
    padding-top: 18px;
  }

  .pricing-choice-card,
  .pricing-choice-card:first-child,
  .pricing-choice-card:last-child {
    min-height: auto;
    margin: 0;
    border-radius: 14px;
  }

  .account-settings-shell {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .account-settings-nav {
    position: static;
    display: grid;
    grid-template-columns: 1fr;
  }

  .resource-toolbar,
  .settings-grid,
  .payment-method-grid {
    grid-template-columns: 1fr;
    align-items: stretch;
    flex-direction: column;
  }

  .pricing-heading,
  .payment-account-heading {
    flex-direction: column;
  }

  .pricing-summary {
    width: 100%;
    text-align: left;
  }

  .pricing-grid,
  .usage-strip,
  .plan-estimate,
  .plan-checkout-summary,
  .platform-payment-summary,
  .contract-profile-grid,
  .plan-payment-grid,
  .plan-overview-grid,
  .split-heading {
    grid-template-columns: 1fr;
  }

  .plan-payment-method {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
}

@media (min-width: 721px) and (max-width: 1180px) {
  .account-plan-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .plans-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .pricing-choice-card,
  .pricing-choice-card:first-child,
  .pricing-choice-card:last-child {
    margin: 0;
    border-radius: 16px;
  }

  .account-settings-shell {
    grid-template-columns: 210px minmax(0, 1fr);
    gap: 18px;
  }

  .pricing-grid,
  .payment-method-grid,
  .plan-checkout-summary,
  .contract-profile-grid,
  .plan-payment-grid,
  .plan-overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .plan-payment-method {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
}
</style>
